import { IconTooltip } from '@/components'
import { cn } from '@/lib/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import { TONE_PILL_CLASSES, formatNumber, gradeTone } from './metrics'
import type { GradeMetric } from './use-metric-texts'
import { useMetricTexts } from './use-metric-texts'

export const GradePill: React.FC<{ grade: number | null | undefined }> = ({
  grade,
}) => {
  if (grade === null || grade === undefined)
    return <span className="text-sm text-stone-400">–</span>

  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold tabular-nums',
        TONE_PILL_CLASSES[gradeTone(grade)]
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {formatNumber(grade)}
    </span>
  )
}

export const InfoLabel: React.FC<{
  label: string
  info: string
  /** Keep the surrounding text style, e.g. inside a table header. */
  plain?: boolean
}> = ({ label, info, plain = false }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5',
      !plain && 'text-xs font-semibold uppercase text-stone-500'
    )}
  >
    {label}
    <IconTooltip
      triggerClassName="text-stone-400 hover:text-stone-600"
      contentClassName="max-w-xs text-xs normal-case font-normal leading-relaxed"
    >
      {info}
    </IconTooltip>
  </span>
)

export const ScoreBar: React.FC<{ score: number; color?: string }> = ({
  score,
  color = 'var(--color-stone-700)',
}) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-200">
    <div
      className="h-full rounded-full"
      style={{ width: `${score}%`, backgroundColor: color }}
    />
  </div>
)

export const PerfectScoreCard: React.FC<{
  metrics: GradeMetric[]
}> = ({ metrics }) => {
  const translations = useTranslate({
    title: 'driver-grade.perfect-title',
    points: ['driver-grade.points-gain', { points: '' }],
  })
  const { tip } = useMetricTexts()

  const ordered = [...metrics].sort((a, b) => b.pointsToGain - a.pointsToGain)

  return (
    <div className="space-y-3 px-5 py-4">
      <div className="text-xs font-semibold uppercase text-stone-500">
        {translations.title}
      </div>
      <ul className="space-y-2">
        {ordered.map(metric => (
          <li
            key={metric.key}
            className="flex items-start justify-between gap-3 text-sm text-stone-900"
          >
            <span>{tip(metric)}</span>
            {metric.pointsToGain > 0 && (
              <span className="shrink-0 font-semibold tabular-nums text-emerald-700">
                {translations.points({
                  points: formatNumber(metric.pointsToGain),
                })}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
